// The in-memory API's operating cadence (MEETINGS; 06 §Cadence "Issues", "Weekly team meeting",
// "Meeting screen", "Vision"; 05 `issue`, `meeting / meeting_series`): the 23 routes of
// `cadenceRoutes`, with the server's rules as the work package states them:
//   - standing: a manager is a company owner/admin (or an agency operator); a team viewer holds
//     a seat that is a member of the team, its lead seat or its human-owner seat;
//   - an issue is seen by managers, the team's viewers, its owner seat's holder, its raiser, and
//     (company-level: no team, or escalated to the company) every member; edited by managers, the
//     owner seat's holder, the team lead's holder and the raiser; escalated by managers, the team
//     lead's holder and the owner seat's holder; anything else is a 404 (or a 403 for a visible
//     issue the caller may not change);
//   - a series is seen by managers, its attendee seats' holders and the team's viewers, and run
//     (meetings, agenda, outcomes) by managers, the facilitator seat's holder and the team lead's;
//   - the agenda is built from the live data (scorecard misses of the last complete week →
//     off-track / at-risk priorities of the quarter → the team's open issues → due to-dos made by
//     the series' meetings or the team's issues → the series' custom lines), replacing the built
//     lines that have no outcome; each line's source card is read live on every read;
//   - the end posts the summary as a system message into the team channel's topic "Meeting
//     YYYY-MM-DD" (the company's date of the meeting) and schedules the next meeting;
//   - to-dos are real Work tasks (through the Work twin), due in 7 days unless given.
// Seeds (on first use, deterministic): Acme's recruiting team has "Recruiting weekly" (an ended
// meeting with its outcomes and summary, an upcoming one with its agenda built), six issues and a
// vision page; HTVA a lighter "Leadership weekly" with three company issues and a vision page.
import { nextAfter, parseRule } from "@companyos/domain-work/recurrence";
import type { Problem, RouteId } from "@companyos/sdk";
import { formatCardMetric } from "../features/format";
import { isoWeekKey } from "../focus/focus";
import { shiftWeek } from "../focus/weeks";
import type { Handler, Principal } from "./fake";
import type { MeetingsChatBridge } from "./fake-chat";
import { COMPANY_IDS, type FakeCompany, uid } from "./fake-data";
import type {
  AgendaItemView,
  AgendaSourceCard,
  DecisionRow,
  DecisionStances,
  Goal,
  IssueView,
  MeetingDetail,
  MeetingOutcomeRow,
  MeetingSeriesView,
  MeetingSummary,
  ScorecardMetric,
  Seat,
  SeatAssignment,
  VisionView,
} from "./types";

type Issue = Omit<
  IssueView,
  | "age_days"
  | "team_name"
  | "owner_title"
  | "owner_name"
  | "raised_by_name"
  | "can_edit"
  | "can_escalate"
>;
type Series = Omit<MeetingSeriesView, "team_name" | "can_edit" | "next_meeting" | "last_meeting">;
type Meeting = MeetingDetail["meeting"];
type Item = Omit<AgendaItemView, "source">;
type Outcome = MeetingOutcomeRow;
type Decision = DecisionRow;
type TaskCard = Extract<AgendaSourceCard, { kind: "task" }>["task"];
type Section = Item["section"];
type SectionKey = "scorecard" | "priorities" | "issues" | "todos" | "headlines" | "custom";
type VisionKey = VisionView["sections"][number]["key"];

export type CadenceTeam = {
  id: string;
  name: string;
  parent_team_id: string | null;
  lead_seat_id: string | null;
  human_owner_seat_id: string | null;
};

/** A digital employee as the pre-read sees it: its seat and whether it can run now. */
export type CadenceAgent = { membership_id: string; name: string; seat_id: string; live: boolean };

export type CadenceDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  seatsOf: (c: FakeCompany) => Seat[];
  assignmentsOf: (c: FakeCompany) => SeatAssignment[];
  teamsOf: (c: FakeCompany) => CadenceTeam[];
  teamMembersOf: (c: FakeCompany) => { team_id: string; seat_id: string }[];
  goalsOf: (c: FakeCompany) => Goal[];
  /** A membership's display name (person or DE). */
  nameOf: (c: FakeCompany, membershipId: string) => string;
  /** The company's digital employees; `live` = status active and the `agents` add-on on. */
  agentsOf: (p: Principal) => CadenceAgent[];
  /** A seat's scorecard metrics for a week as the caller reads them (empty: no scorecard). */
  seatMetrics: (p: Principal, seatId: string, week: string) => ScorecardMetric[];
  chat: () => MeetingsChatBridge | null;
  /** The Work twin: a to-do made as a task, and a task read as an agenda card. */
  tasks: {
    create: (
      p: Principal,
      input: {
        title: string;
        assignee_seat_id: string | null;
        due_at: string;
        origin: "meeting" | "issue";
        origin_ref: string;
        board_id: string | null;
      },
    ) => TaskCard | null;
    get: (p: Principal, id: string) => TaskCard | null;
  };
  /** FOUNDER-OS: hands the portfolio twin the issues escalated to the portfolio. */
  share?: (bridge: CadencePortfolioBridge) => void;
};

/** FOUNDER-OS (fake-portfolio.ts): a company's issues at `portfolio`, read and seeded. */
export type CadencePortfolioBridge = {
  /** Open issues escalated to the portfolio, as `p` (a manager of the company) reads them. */
  escalated: (p: Principal) => IssueView[];
  /** Seeds an open issue already escalated to the portfolio (the founder's demo world). */
  seedEscalated: (
    p: Principal,
    v: {
      title: string;
      body: string;
      priority: number;
      status: IssueView["status"];
      daysAgo: number;
    },
  ) => void;
};

/** Every route this twin serves (the contract test checks it is all of `cadenceRoutes`). */
export const FAKE_CADENCE_ROUTES = [
  "issues.list",
  "issues.create",
  "issues.update",
  "issues.delete",
  "issues.escalate",
  "issues.resolve",
  "meetingSeries.list",
  "meetingSeries.create",
  "meetingSeries.update",
  "meetings.list",
  "meetings.create",
  "meetings.get",
  "meetings.update",
  "meetings.buildAgenda",
  "meetings.addItem",
  "meetings.updateItem",
  "meetings.removeItem",
  "meetings.start",
  "meetings.cancel",
  "meetings.end",
  "meetings.recordOutcome",
  "meetings.requestPreread",
  "vision.get",
  "vision.update",
  "decisionStances.list",
  "decisionStances.set",
] as const satisfies readonly RouteId[];

const SEED_AT = "2026-06-01T09:00:00Z";
const DAY = 86_400_000;
const COLUMNS = ["identify", "discuss", "solve"] as const;
const ORDER: readonly SectionKey[] = [
  "scorecard",
  "priorities",
  "issues",
  "todos",
  "headlines",
  "custom",
];
const AUTO = new Set<Section>(["scorecard", "priorities", "issues", "todos"]);
/** Default time boxes per section, in seconds (issues: the whole block, split per line). */
export const DEFAULT_BOXES: Record<SectionKey, number> = {
  scorecard: 300,
  priorities: 300,
  issues: 1800,
  todos: 300,
  headlines: 300,
  custom: 300,
};
const VISION: readonly { key: VisionKey; label: string }[] = [
  { key: "core_values", label: "Core values" },
  { key: "focus", label: "Focus" },
  { key: "long_term_target", label: "Long-term target" },
  { key: "three_year_picture", label: "3-year picture" },
  { key: "one_year_plan", label: "1-year plan" },
];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type VisionState = {
  page_id: string | null;
  version: number;
  updated_at: string | null;
  sections: Record<VisionKey, string>;
};

type Store = {
  issues: Issue[];
  series: Series[];
  meetings: Meeting[];
  items: Item[];
  outcomes: Outcome[];
  decisions: Decision[];
  vision: VisionState;
  /** To-dos (task ids) a meeting or an issue made, by what made them. */
  todos: { task_id: string; series_id: string | null; team_id: string | null }[];
};

const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 0x0fff;

/** `YYYY-MM-DD` of an instant in a time zone. */
export function localDate(at: string | Date, zone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(typeof at === "string" ? new Date(at) : at);
}

export function createCadenceFake(d: CadenceDeps): Partial<Record<RouteId, Handler>> {
  const { json, problem, notFound, validation } = d;
  const forbidden = () => problem(403, "forbidden", "Forbidden");
  const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
  const noContent = () => new Response(null, { status: 204 });
  const stores = new Map<string, Store>();
  let seq = 0;
  const stamp = () => d.now().toISOString();
  const zoneOf = (c: FakeCompany) => c.calendar_config?.timezone ?? "UTC";

  // Standing ------------------------------------------------------------------------------------

  const mineOf = (p: Principal) =>
    new Set(
      p.me.memberships.filter((m) => m.company_id === p.company.id).map((m) => m.membership_id),
    );
  const meOf = (p: Principal) => [...mineOf(p)][0] ?? null;
  function heldBy(p: Principal): Set<string> {
    const mine = mineOf(p);
    return new Set(
      d
        .assignmentsOf(p.company)
        .filter((a) => a.active_to === null && mine.has(a.membership_id))
        .map((a) => a.seat_id),
    );
  }
  const teamOf = (c: FakeCompany, id: string | null) =>
    id ? (d.teamsOf(c).find((t) => t.id === id) ?? null) : null;
  function teamSeats(c: FakeCompany, team: CadenceTeam): string[] {
    const out = new Set<string>();
    if (team.lead_seat_id) out.add(team.lead_seat_id);
    for (const m of d.teamMembersOf(c)) if (m.team_id === team.id) out.add(m.seat_id);
    return [...out];
  }
  function teamViewer(p: Principal, team: CadenceTeam | null, held = heldBy(p)): boolean {
    if (!team) return false;
    return (
      (team.lead_seat_id !== null && held.has(team.lead_seat_id)) ||
      (team.human_owner_seat_id !== null && held.has(team.human_owner_seat_id)) ||
      d.teamMembersOf(p.company).some((m) => m.team_id === team.id && held.has(m.seat_id))
    );
  }
  const leadHolder = (_p: Principal, team: CadenceTeam | null, held: Set<string>) =>
    !!team?.lead_seat_id && held.has(team.lead_seat_id);
  const isMember = (p: Principal) => p.roles.some((r) => r !== "client_viewer");

  function issueSees(p: Principal, i: Issue, held = heldBy(p)): boolean {
    if (p.manager) return true;
    if (i.team_id === null || i.escalated_to !== "team") return true;
    if (teamViewer(p, teamOf(p.company, i.team_id), held)) return true;
    if (i.owner_seat_id && held.has(i.owner_seat_id)) return true;
    return i.raised_by_membership_id !== null && mineOf(p).has(i.raised_by_membership_id);
  }
  function issueEdits(p: Principal, i: Issue, held = heldBy(p)): boolean {
    if (p.manager) return true;
    if (i.owner_seat_id && held.has(i.owner_seat_id)) return true;
    if (leadHolder(p, teamOf(p.company, i.team_id), held)) return true;
    return i.raised_by_membership_id !== null && mineOf(p).has(i.raised_by_membership_id);
  }
  function issueEscalates(p: Principal, i: Issue, held = heldBy(p)): boolean {
    if (p.manager) return true;
    if (leadHolder(p, teamOf(p.company, i.team_id), held)) return true;
    return !!i.owner_seat_id && held.has(i.owner_seat_id);
  }
  function canRaise(p: Principal, teamId: string | null, held = heldBy(p)): boolean {
    if (teamId === null) return isMember(p) || p.manager;
    return p.manager || teamViewer(p, teamOf(p.company, teamId), held);
  }
  function seriesSees(p: Principal, s: Series, held = heldBy(p)): boolean {
    if (p.manager) return true;
    if (s.attendees.some((a) => held.has(a))) return true;
    return teamViewer(p, teamOf(p.company, s.team_id), held);
  }
  function seriesRuns(p: Principal, s: Series, held = heldBy(p)): boolean {
    if (p.manager) return true;
    if (s.facilitator_seat_id && held.has(s.facilitator_seat_id)) return true;
    return leadHolder(p, teamOf(p.company, s.team_id), held);
  }

  // Names ---------------------------------------------------------------------------------------

  const seatOf = (c: FakeCompany, id: string | null) =>
    id ? (d.seatsOf(c).find((s) => s.id === id) ?? null) : null;
  function holderName(c: FakeCompany, seatId: string): string | null {
    const a = d.assignmentsOf(c).find((x) => x.seat_id === seatId && x.active_to === null);
    return a ? d.nameOf(c, a.membership_id) : null;
  }
  const ageDays = (i: Issue) =>
    Math.max(
      0,
      Math.floor(
        ((i.resolved_at ? Date.parse(i.resolved_at) : d.now().getTime()) -
          Date.parse(i.created_at)) /
          DAY,
      ),
    );
  function issueView(p: Principal, i: Issue, held = heldBy(p)): IssueView {
    const owner = seatOf(p.company, i.owner_seat_id);
    return {
      ...i,
      age_days: ageDays(i),
      team_name: teamOf(p.company, i.team_id)?.name ?? null,
      owner_title: owner?.title ?? null,
      owner_name: owner ? holderName(p.company, owner.id) : null,
      raised_by_name: i.raised_by_membership_id
        ? d.nameOf(p.company, i.raised_by_membership_id)
        : null,
      can_edit: issueEdits(p, i, held),
      can_escalate: issueEscalates(p, i, held),
    };
  }

  // Store and seed ------------------------------------------------------------------------------

  const nextId = (c: FakeCompany, group: number) =>
    uid(0xca00 + group, companyNo(c.id) * 0x100000 + ++seq);
  const actorOf = (p: Principal | null) =>
    p === null
      ? { actor_kind: "system" as const, actor_membership_id: null }
      : p.operator && !meOf(p)
        ? { actor_kind: "agency_user" as const, actor_membership_id: null }
        : { actor_kind: "person" as const, actor_membership_id: meOf(p) };

  function emptyVision(): VisionState {
    return {
      page_id: null,
      version: 0,
      updated_at: null,
      sections: {
        core_values: "",
        focus: "",
        long_term_target: "",
        three_year_picture: "",
        one_year_plan: "",
      },
    };
  }

  /** RECALL-KPIS-MIRROR: stances per decision id (decision ids are unique across companies). */
  const stances = new Map<string, DecisionStances["stances"]>();

  function storeOf(p: Principal): Store {
    let s = stores.get(p.company.id);
    if (!s) {
      s = {
        issues: [],
        series: [],
        meetings: [],
        items: [],
        outcomes: [],
        decisions: [],
        vision: emptyVision(),
        todos: [],
      };
      stores.set(p.company.id, s);
      if (p.company.id === COMPANY_IDS.acme) seedAcme(p, s);
      else if (p.company.id === COMPANY_IDS.htva) seedHtva(p, s);
    }
    return s;
  }

  function newIssue(
    p: Principal,
    s: Store,
    v: Partial<Issue> & { title: string },
    by: Principal | null,
  ): Issue {
    const at = v.created_at ?? stamp();
    const status = v.status ?? "identify";
    const row: Issue = {
      company_id: p.company.id,
      id: nextId(p.company, 1),
      team_id: null,
      owner_seat_id: null,
      raised_by_membership_id: by ? meOf(by) : null,
      body: null,
      priority: 2,
      status,
      position: 0,
      resolution_kind: null,
      resolution_refs: [],
      resolution_note: null,
      escalated_to: "team",
      escalation_ref: null,
      parent_issue_id: null,
      thread_id: null,
      source_kind: "manual",
      source_id: null,
      source_label: null,
      resolved_at: null,
      archived_at: null,
      ...actorOf(by),
      created_at: at,
      updated_at: at,
      ...v,
    };
    s.issues.push(row);
    return row;
  }

  function newSeries(
    p: Principal,
    s: Store,
    v: Partial<Series> & { name: string; schedule: string },
    by: Principal | null,
  ): Series {
    const at = v.created_at ?? stamp();
    const row: Series = {
      company_id: p.company.id,
      id: nextId(p.company, 2),
      team_id: null,
      kind: "weekly",
      duration_minutes: 60,
      attendees: [],
      facilitator_seat_id: null,
      agenda_config: {},
      channel_id: null,
      board_id: null,
      preread_by_membership_id: null,
      archived_at: null,
      ...actorOf(by),
      created_at: at,
      updated_at: at,
      ...v,
    };
    s.series.push(row);
    return row;
  }

  function newMeeting(
    p: Principal,
    s: Store,
    series: Series,
    scheduledAt: string,
    by: Principal | null,
  ): Meeting {
    const at = stamp();
    const row: Meeting = {
      company_id: p.company.id,
      id: nextId(p.company, 3),
      series_id: series.id,
      scheduled_at: scheduledAt,
      started_at: null,
      ended_at: null,
      state: "scheduled",
      notes: "",
      rating: null,
      thread_id: null,
      video_note_id: null,
      agenda_built_at: null,
      current_item_id: null,
      item_started_at: null,
      summary_message_id: null,
      preread_state: "none",
      preread_wakeup_id: null,
      preread_requested_at: null,
      preread_note: null,
      ...actorOf(by),
      created_at: at,
      updated_at: at,
    };
    s.meetings.push(row);
    return row;
  }

  function newItem(
    p: Principal,
    s: Store,
    m: Meeting,
    v: Partial<Item> & { section: Section; title: string },
    by: Principal | null,
  ): Item {
    const at = stamp();
    const row: Item = {
      company_id: p.company.id,
      id: nextId(p.company, 4),
      meeting_id: m.id,
      source_kind: "none",
      source_id: null,
      seat_id: null,
      detail: null,
      time_box_s: 300,
      elapsed_s: 0,
      position: s.items.filter((i) => i.meeting_id === m.id).length,
      done_at: null,
      outcome_kind: "none",
      outcome_ref: null,
      ...actorOf(by),
      created_at: at,
      updated_at: at,
      ...v,
    };
    s.items.push(row);
    return row;
  }

  function newDecision(
    p: Principal,
    s: Store,
    v: {
      title: string;
      decision: string;
      why: string | null;
      source: "meeting" | "issue";
      ref: string;
    },
    by: Principal | null,
    at = stamp(),
  ): Decision {
    const row: Decision = {
      company_id: p.company.id,
      id: nextId(p.company, 6),
      title: v.title,
      context: null,
      options: [],
      decision: v.decision,
      why: v.why,
      decided_by_seat_id: null,
      review_at: null,
      expected_outcome: null,
      outcome_card_at: null,
      outcome: null,
      outcome_at: null,
      outcome_by_membership_id: null,
      outcome_notice: null,
      source: v.source,
      source_ref: v.ref,
      share_to_portfolio: false,
      recall_ref: null,
      ...actorOf(by),
      created_at: at,
      updated_at: at,
    };
    s.decisions.push(row);
    return row;
  }

  function newOutcome(
    p: Principal,
    s: Store,
    m: Meeting,
    v: { kind: Outcome["kind"]; ref_id: string; title: string; item_id: string | null },
    by: Principal | null,
    at = stamp(),
  ): Outcome {
    const row: Outcome = {
      company_id: p.company.id,
      id: nextId(p.company, 5),
      meeting_id: m.id,
      agenda_item_id: v.item_id,
      kind: v.kind,
      ref_id: v.ref_id,
      title: v.title,
      ...actorOf(by),
      created_at: at,
    };
    s.outcomes.push(row);
    const item = v.item_id ? s.items.find((i) => i.id === v.item_id) : undefined;
    if (item) {
      item.outcome_kind = v.kind === "task" ? "task" : v.kind === "decision" ? "decision" : "issue";
      item.outcome_ref = v.ref_id;
      item.updated_at = at;
    }
    return row;
  }

  /** A to-do: a Work task (origin meeting/issue), due in 7 days unless given. */
  function makeTodo(
    p: Principal,
    s: Store,
    v: { title: string; assignee_seat_id: string | null; due_at: string | null },
    origin: {
      kind: "meeting" | "issue";
      ref: string;
      series: Series | null;
      team_id: string | null;
    },
  ): string {
    const due = v.due_at ?? new Date(d.now().getTime() + 7 * DAY).toISOString();
    const task = d.tasks.create(p, {
      title: v.title,
      assignee_seat_id: v.assignee_seat_id,
      due_at: due,
      origin: origin.kind,
      origin_ref: origin.ref,
      board_id: origin.series?.board_id ?? null,
    });
    // No board to file into: the to-do keeps an id of its own (the outcome still records it).
    const id = task?.id ?? nextId(p.company, 7);
    s.todos.push({
      task_id: id,
      series_id: origin.series?.id ?? null,
      team_id: origin.team_id,
    });
    return id;
  }

  const ruleOf = (schedule: string) => {
    const r = parseRule(schedule);
    return r.ok ? r.rule : null;
  };
  function nextOccurrence(c: FakeCompany, series: Series, after: Date): string | null {
    const rule = ruleOf(series.schedule);
    if (!rule) return null;
    const at = nextAfter(rule, new Date(series.created_at), zoneOf(c), after);
    return at ? at.toISOString() : null;
  }

  const lastCompleteWeek = () => shiftWeek(isoWeekKey(d.now()), -1);

  function seedAcme(p: Principal, s: Store) {
    const c = p.company;
    const team = d.teamsOf(c).find((t) => t.parent_team_id === null) ?? d.teamsOf(c)[0] ?? null;
    const seats = team ? teamSeats(c, team) : [];
    const channel = d.chat()?.teamChannel(c.id) ?? null;
    const series = newSeries(
      p,
      s,
      {
        name: "Recruiting weekly",
        team_id: team?.id ?? null,
        schedule: "FREQ=WEEKLY;BYDAY=TU;BYHOUR=10;BYMINUTE=0",
        duration_minutes: 60,
        attendees: seats,
        facilitator_seat_id: team?.lead_seat_id ?? null,
        agenda_config: { custom: ["Wins of the week"] },
        channel_id: channel?.id ?? null,
        created_at: SEED_AT,
      },
      null,
    );
    const upcoming =
      nextOccurrence(c, series, d.now()) ?? new Date(d.now().getTime() + 3 * DAY).toISOString();
    // The occurrence before `upcoming`: search from 8 days back, since a DST change makes that
    // week 7 days ± 1 hour (7 days − 1 minute would land on `upcoming` itself).
    const pastAt =
      nextOccurrence(c, series, new Date(Date.parse(upcoming) - 8 * DAY)) ??
      new Date(Date.parse(upcoming) - 7 * DAY).toISOString();
    const ago = (days: number) => new Date(d.now().getTime() - days * DAY).toISOString();

    // Six issues: two to identify, two to discuss, one to solve (escalated), one resolved.
    const week = lastCompleteWeek();
    const deSeat = seats.find((id) => id !== team?.lead_seat_id) ?? null;
    // The metric issue comes from a team seat that missed a target last week. The scorecard
    // noise is keyed by week, so in some weeks the first DE seat meets every target: look across
    // the team's non-lead seats (the first DE seat first) rather than drop to a manual issue.
    const missOf = (seat: string) =>
      d.seatMetrics(p, seat, week).find((m) => m.attainment !== null && m.attainment < 1) ?? null;
    const missedSeat =
      [deSeat, ...seats.filter((id) => id !== deSeat && id !== team?.lead_seat_id)].find(
        (id): id is string => id !== null && missOf(id) !== null,
      ) ?? deSeat;
    const missed = missedSeat ? missOf(missedSeat) : null;
    const message = channel ? (d.chat()?.firstMessage(c.id, channel.id) ?? null) : null;
    const lead = team?.lead_seat_id ?? null;
    const base = { team_id: team?.id ?? null };
    newIssue(
      p,
      s,
      {
        ...base,
        title: missed ? `${missed.name} below target` : "Offer acceptance slipping on senior roles",
        status: "identify",
        position: 0,
        priority: 1,
        owner_seat_id: missedSeat,
        source_kind: missed ? "metric" : "manual",
        source_id: missed?.metric_def_id ?? null,
        source_label: missed
          ? `${missed.name} · W${week.slice(6)}: ${formatCardMetric(missed.actual, missed.unit)} of ${formatCardMetric(missed.target, missed.unit)}`
          : null,
        created_at: ago(2),
      },
      null,
    );
    newIssue(
      p,
      s,
      {
        ...base,
        title: "Client feedback on shortlists takes 5+ days",
        status: "identify",
        position: 1,
        priority: 2,
        owner_seat_id: lead,
        source_kind: message ? "message" : "manual",
        source_id: message?.id ?? null,
        source_label: message && channel ? `Message in #${channel.name}` : null,
        created_at: ago(4),
      },
      null,
    );
    newIssue(
      p,
      s,
      {
        ...base,
        title: "Interview no-shows on Thursday slots",
        status: "discuss",
        position: 0,
        priority: 2,
        owner_seat_id: deSeat,
        created_at: ago(6),
      },
      null,
    );
    newIssue(
      p,
      s,
      {
        ...base,
        title: "Sourcing tool licences up for renewal",
        status: "discuss",
        position: 1,
        priority: 3,
        owner_seat_id: lead,
        created_at: ago(9),
      },
      null,
    );
    newIssue(
      p,
      s,
      {
        ...base,
        title: "Northwind wants a weekly pipeline report",
        status: "solve",
        position: 0,
        priority: 1,
        owner_seat_id: lead,
        escalated_to: "company",
        created_at: ago(11),
      },
      null,
    );
    const resolved = newIssue(
      p,
      s,
      {
        ...base,
        title: "Duplicate candidates in the tracker",
        status: "resolved",
        position: 0,
        priority: 2,
        owner_seat_id: lead,
        created_at: ago(14),
        resolved_at: new Date(Date.parse(pastAt) + 30 * 60_000).toISOString(),
      },
      null,
    );

    // The ended meeting: its agenda, notes, rating 8, outcomes and the summary in chat.
    const past = newMeeting(p, s, series, pastAt, null);
    const endedAt = new Date(Date.parse(pastAt) + 55 * 60_000).toISOString();
    Object.assign(past, {
      state: "ended",
      started_at: pastAt,
      ended_at: endedAt,
      agenda_built_at: new Date(Date.parse(pastAt) - 3600_000).toISOString(),
      notes:
        "Pipeline is healthy for Q4. Northwind asked for a weekly report; Jordan to draft a template. Duplicates in the tracker solved by a merge rule.",
      rating: 8,
      preread_state: "ready",
      preread_note:
        "Riley's pre-read: 14 candidates in play, 3 offers out, 1 accepted. Screens are a day behind on the controller search.",
      preread_requested_at: new Date(Date.parse(pastAt) - DAY).toISOString(),
    } satisfies Partial<Meeting>);
    const wins = newItem(
      p,
      s,
      past,
      {
        section: "custom",
        title: "Wins of the week",
        time_box_s: 300,
        elapsed_s: 240,
        position: 1,
      },
      null,
    );
    wins.done_at = endedAt;
    const dupItem = newItem(
      p,
      s,
      past,
      {
        section: "issues",
        title: resolved.title,
        source_kind: "issue",
        source_id: resolved.id,
        seat_id: lead,
        time_box_s: 1800,
        elapsed_s: 1260,
        position: 0,
      },
      null,
    );
    dupItem.done_at = endedAt;
    const origin = { kind: "meeting" as const, ref: past.id, series, team_id: team?.id ?? null };
    const todoA = makeTodo(
      p,
      s,
      {
        title: "Draft the Northwind weekly report template",
        assignee_seat_id: lead,
        due_at: new Date(Date.parse(pastAt) + 6 * DAY).toISOString(),
      },
      origin,
    );
    const todoB = makeTodo(
      p,
      s,
      {
        title: "Add a merge rule for duplicate candidates",
        assignee_seat_id: deSeat,
        due_at: new Date(Date.parse(pastAt) + 7 * DAY).toISOString(),
      },
      origin,
    );
    newOutcome(
      p,
      s,
      past,
      {
        kind: "task",
        ref_id: todoA,
        title: "Draft the Northwind weekly report template",
        item_id: null,
      },
      null,
      endedAt,
    );
    newOutcome(
      p,
      s,
      past,
      {
        kind: "task",
        ref_id: todoB,
        title: "Add a merge rule for duplicate candidates",
        item_id: dupItem.id,
      },
      null,
      endedAt,
    );
    const decision = newDecision(
      p,
      s,
      {
        title: resolved.title,
        decision: "The tracker merges candidates by email; recruiters stop keeping side lists.",
        why: "Two recruiters called the same candidate twice last month.",
        source: "meeting",
        ref: past.id,
      },
      null,
      endedAt,
    );
    newOutcome(
      p,
      s,
      past,
      { kind: "decision", ref_id: decision.id, title: decision.decision, item_id: null },
      null,
      endedAt,
    );
    Object.assign(resolved, {
      resolution_kind: "decision",
      resolution_refs: [{ kind: "decision" as const, id: decision.id }],
      resolution_note: "Solved in the weekly meeting.",
    });
    newOutcome(
      p,
      s,
      past,
      { kind: "issue_resolved", ref_id: resolved.id, title: resolved.title, item_id: dupItem.id },
      null,
      endedAt,
    );
    if (channel) {
      const posted = d
        .chat()
        ?.postSummary(c.id, channel.id, topicName(c, past), summaryOf(p, s, series, past), endedAt);
      past.summary_message_id = posted?.message_id ?? null;
    }

    // The upcoming meeting: its agenda built from the live data.
    const next = newMeeting(p, s, series, upcoming, null);
    buildAgenda(p, s, next);

    s.vision = {
      page_id: nextId(c, 8),
      version: 1,
      updated_at: SEED_AT,
      sections: {
        core_values:
          "Candidates first: every candidate hears back within two days.\nSay what we see: numbers on the table, every week.\nPeople and digital employees share one queue.",
        focus:
          "Purpose: help growing companies hire the people who change them.\nNiche: finance and operations hires for mid-size food and retail firms.",
        long_term_target: "500 placements a year by 2032, with every client renewing.",
        three_year_picture:
          "Revenue of $6M, 40 clients on retainer, a team of 12 people and 8 digital employees, and a time-to-offer of 18 days.",
        one_year_plan:
          "Revenue of $2.4M, 120 placements, two new retained clients a quarter, and every recruiter paired with a digital employee.",
      },
    };
  }

  function seedHtva(p: Principal, s: Store) {
    const c = p.company;
    const channel = d.chat()?.teamChannel(c.id) ?? null;
    const series = newSeries(
      p,
      s,
      {
        name: "Leadership weekly",
        schedule: "FREQ=WEEKLY;BYDAY=MO;BYHOUR=9;BYMINUTE=0",
        duration_minutes: 45,
        channel_id: channel?.id ?? null,
        created_at: SEED_AT,
      },
      null,
    );
    const ago = (days: number) => new Date(d.now().getTime() - days * DAY).toISOString();
    newIssue(
      p,
      s,
      { title: "Q4 hiring plan not signed off", priority: 1, created_at: ago(3) },
      null,
    );
    newIssue(
      p,
      s,
      {
        title: "Onboarding checklist out of date",
        status: "discuss",
        priority: 2,
        created_at: ago(8),
      },
      null,
    );
    newIssue(
      p,
      s,
      { title: "Office lease renewal", priority: 3, position: 1, created_at: ago(12) },
      null,
    );
    const upcoming =
      nextOccurrence(c, series, d.now()) ?? new Date(d.now().getTime() + 2 * DAY).toISOString();
    buildAgenda(p, s, newMeeting(p, s, series, upcoming, null));
    s.vision = {
      page_id: nextId(c, 8),
      version: 1,
      updated_at: SEED_AT,
      sections: {
        core_values: "Honest numbers.\nClients' success is ours.\nSmall teams, clear owners.",
        focus: "Run the operating system our client companies grow on.",
        long_term_target: "100 companies running on CompanyOS by 2030.",
        three_year_picture:
          "30 client companies, each with a weekly scorecard their founder reads.",
        one_year_plan:
          "10 client companies live, and every one of them meeting weekly in CompanyOS.",
      },
    };
  }

  // Agenda ----------------------------------------------------------------------------------

  const itemsOf = (s: Store, m: Meeting) =>
    s.items
      .filter((i) => i.meeting_id === m.id)
      .sort(
        (a, b) =>
          ORDER.indexOf(a.section as SectionKey) - ORDER.indexOf(b.section as SectionKey) ||
          a.position - b.position,
      );
  function renumber(s: Store, m: Meeting) {
    itemsOf(s, m).forEach((i, n) => {
      i.position = n;
    });
  }
  function meetingSeats(c: FakeCompany, series: Series): string[] {
    const team = teamOf(c, series.team_id);
    return [...new Set([...series.attendees, ...(team ? teamSeats(c, team) : [])])];
  }
  const openIssue = (i: Issue) =>
    !i.archived_at && (COLUMNS as readonly string[]).includes(i.status);

  function buildAgenda(p: Principal, s: Store, m: Meeting) {
    const c = p.company;
    const series = s.series.find((x) => x.id === m.series_id);
    if (!series) return;
    const seats = meetingSeats(c, series);
    const keep = (i: Item) => !AUTO.has(i.section) || i.outcome_kind !== "none";
    s.items = s.items.filter((i) => i.meeting_id !== m.id || keep(i));
    const kept = s.items.filter((i) => i.meeting_id === m.id);
    const taken = new Set(
      kept.flatMap((i) => (i.source_id ? [`${i.section}:${i.source_id}`] : [])),
    );
    const boxes = { ...DEFAULT_BOXES, ...(series.agenda_config.time_boxes ?? {}) };
    const drafts: Record<SectionKey, (Partial<Item> & { title: string })[]> = {
      scorecard: [],
      priorities: [],
      issues: [],
      todos: [],
      headlines: [],
      custom: [],
    };
    const add = (section: SectionKey, v: Partial<Item> & { title: string }) => {
      if (v.source_id && taken.has(`${section}:${v.source_id}`)) return;
      drafts[section].push(v);
    };
    // 1. Scorecard misses of the last complete week.
    const week = lastCompleteWeek();
    for (const seat of seats)
      for (const metric of d.seatMetrics(p, seat, week))
        if (metric.attainment !== null && metric.attainment < 1)
          add("scorecard", {
            title: metric.name,
            detail: `${formatCardMetric(metric.actual, metric.unit)} of ${formatCardMetric(metric.target, metric.unit)} · W${week.slice(6)}`,
            source_kind: "metric",
            source_id: metric.metric_def_id,
            seat_id: seat,
          });
    // 2. Off-track and at-risk priorities of the quarter.
    const now = d.now();
    const quarter = `${now.getUTCFullYear()}-Q${Math.floor(now.getUTCMonth() / 3) + 1}`;
    for (const g of d.goalsOf(c))
      if (
        g.kind === "priority" &&
        g.period_key === quarter &&
        g.state === "open" &&
        (g.track === "off" || g.track === "at_risk") &&
        seats.includes(g.seat_id)
      )
        add("priorities", {
          title: g.title,
          detail: g.track === "off" ? "Off track" : "At risk",
          source_kind: "goal",
          source_id: g.id,
          seat_id: g.seat_id,
        });
    // 3. The team's open issues, by priority then age, at most 7.
    s.issues
      .filter((i) => openIssue(i) && i.team_id === series.team_id)
      .sort((a, b) => a.priority - b.priority || a.created_at.localeCompare(b.created_at))
      .slice(0, 7)
      .forEach((i) => {
        add("issues", {
          title: i.title,
          detail: i.source_label,
          source_kind: "issue",
          source_id: i.id,
          seat_id: i.owner_seat_id,
        });
      });
    // 4. Due to-dos the series' meetings or the team's issues made, or assigned to its seats.
    const day = localDate(m.scheduled_at, zoneOf(c));
    for (const t of s.todos) {
      if (t.series_id !== series.id && (t.team_id === null || t.team_id !== series.team_id))
        continue;
      const card = d.tasks.get(p, t.task_id);
      if (!card || card.done || !card.due_at) continue;
      if (localDate(card.due_at, zoneOf(c)) > day) continue;
      add("todos", {
        title: card.title,
        detail: `#${card.number} · due ${localDate(card.due_at, zoneOf(c))}`,
        source_kind: "task",
        source_id: card.id,
        seat_id: card.assignee_seat_ids[0] ?? null,
      });
    }
    // 5. The series' standing custom lines.
    for (const title of series.agenda_config.custom ?? [])
      if (!kept.some((i) => i.section === "custom" && i.title === title)) add("custom", { title });

    for (const section of ORDER) {
      const list = drafts[section];
      if (!list.length) continue;
      const count = list.length + kept.filter((i) => i.section === section).length;
      const per = Math.max(
        section === "issues" ? 300 : 60,
        Math.floor((boxes[section] ?? DEFAULT_BOXES[section]) / count),
      );
      let pos = Math.max(-1, ...kept.filter((i) => i.section === section).map((i) => i.position));
      for (const v of list)
        newItem(p, s, m, { ...v, section, time_box_s: per, position: ++pos }, null);
    }
    renumber(s, m);
    m.agenda_built_at = stamp();
    m.updated_at = m.agenda_built_at;
  }

  /** The live card of an item's source, as the caller may read it (null: gone or hidden). */
  function sourceOf(p: Principal, s: Store, i: Item): AgendaSourceCard | null {
    const c = p.company;
    if (!i.source_id) return null;
    if (i.source_kind === "metric") {
      if (!i.seat_id) return null;
      const seat = seatOf(c, i.seat_id);
      const week = lastCompleteWeek();
      const metric = d.seatMetrics(p, i.seat_id, week).find((x) => x.metric_def_id === i.source_id);
      if (!seat || !metric) return null;
      return {
        kind: "metric",
        metric_def_id: metric.metric_def_id,
        name: metric.name,
        unit: metric.unit,
        seat_id: seat.id,
        seat_title: seat.title,
        holder_name: holderName(c, seat.id),
        period_key: week,
        actual: metric.actual,
        target: metric.target,
        attainment: metric.attainment,
        trend: metric.trend.slice(-8).map((t) => ({
          period_key: t.period_key,
          actual: t.actual,
          target: t.target,
        })),
      };
    }
    if (i.source_kind === "goal") {
      const g = d.goalsOf(c).find((x) => x.id === i.source_id);
      const seat = g ? seatOf(c, g.seat_id) : null;
      if (!g || !seat) return null;
      return {
        kind: "goal",
        goal: { ...g, milestones: [...g.milestones].sort((a, b) => a.position - b.position) },
        seat_title: seat.title,
        holder_name: holderName(c, seat.id),
      };
    }
    if (i.source_kind === "issue") {
      const issue = s.issues.find((x) => x.id === i.source_id);
      return issue && issueSees(p, issue) ? { kind: "issue", issue: issueView(p, issue) } : null;
    }
    if (i.source_kind === "task") {
      const task = d.tasks.get(p, i.source_id);
      return task ? { kind: "task", task } : null;
    }
    return null;
  }

  // Meetings --------------------------------------------------------------------------------

  const topicName = (c: FakeCompany, m: Meeting) =>
    `Meeting ${localDate(m.scheduled_at, zoneOf(c))}`;

  function summaryOf(p: Principal, s: Store, series: Series, m: Meeting): string {
    const outs = s.outcomes.filter((o) => o.meeting_id === m.id);
    const lines = (kind: Outcome["kind"]) =>
      outs.filter((o) => o.kind === kind).map((o) => `- ${o.title}`);
    const todos = lines("task");
    const decisions = s.decisions
      .filter((x) => x.source === "meeting" && x.source_ref === m.id)
      .map((x) => `- ${x.decision}`);
    const solved = lines("issue_resolved");
    const raised = lines("issue_raised");
    const items = itemsOf(s, m);
    const out = [
      `**${series.name}** · ${localDate(m.scheduled_at, zoneOf(p.company))}${m.rating ? ` · rated ${m.rating}/10` : ""}`,
      `${items.filter((i) => i.done_at).length} of ${items.length} agenda items covered.`,
    ];
    if (todos.length) out.push("", "**To-dos**", ...todos);
    if (decisions.length) out.push("", "**Decisions**", ...decisions);
    if (solved.length) out.push("", "**Issues solved**", ...solved);
    if (raised.length) out.push("", "**Issues raised**", ...raised);
    if (m.notes.trim()) out.push("", "**Notes**", m.notes.trim());
    return out.join("\n");
  }

  function summary(s: Store, m: Meeting): MeetingSummary {
    return {
      id: m.id,
      series_id: m.series_id,
      scheduled_at: m.scheduled_at,
      started_at: m.started_at,
      ended_at: m.ended_at,
      state: m.state,
      rating: m.rating,
      items: s.items.filter((i) => i.meeting_id === m.id).length,
      outcomes: s.outcomes.filter((o) => o.meeting_id === m.id).length,
      preread_state: m.preread_state,
    };
  }
  const meetingsOf = (s: Store, series: Series) =>
    s.meetings
      .filter((m) => m.series_id === series.id)
      .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));

  function seriesView(p: Principal, s: Store, series: Series, held = heldBy(p)): MeetingSeriesView {
    const list = meetingsOf(s, series);
    const live = list.find((m) => m.state === "live");
    const next = live ?? list.find((m) => m.state === "scheduled");
    const last = [...list].reverse().find((m) => m.state === "ended");
    return {
      ...series,
      team_name: teamOf(p.company, series.team_id)?.name ?? null,
      can_edit: seriesRuns(p, series, held),
      next_meeting: next ? summary(s, next) : null,
      last_meeting: last ? summary(s, last) : null,
    };
  }

  function prereadAgent(p: Principal, series: Series): CadenceAgent | null {
    const agents = d.agentsOf(p);
    if (series.preread_by_membership_id)
      return agents.find((a) => a.membership_id === series.preread_by_membership_id) ?? null;
    const team = teamOf(p.company, series.team_id);
    return (
      agents.find((a) => series.attendees.includes(a.seat_id)) ??
      (team ? agents.find((a) => teamSeats(p.company, team).includes(a.seat_id)) : undefined) ??
      null
    );
  }

  function detail(p: Principal, s: Store, m: Meeting): MeetingDetail {
    const series = s.series.find((x) => x.id === m.series_id) as Series;
    const list = meetingsOf(s, series);
    const at = list.findIndex((x) => x.id === m.id);
    const agent = prereadAgent(p, series);
    const channelId = series.channel_id ?? d.chat()?.teamChannel(p.company.id)?.id ?? null;
    const channel = channelId ? (d.chat()?.channel(p.company.id, channelId) ?? null) : null;
    return {
      meeting: { ...m },
      series: { ...series, team_name: teamOf(p.company, series.team_id)?.name ?? null },
      agenda: itemsOf(s, m).map((i) => ({ ...i, source: sourceOf(p, s, i) })),
      outcomes: s.outcomes
        .filter((o) => o.meeting_id === m.id)
        .sort((a, b) => a.created_at.localeCompare(b.created_at)),
      decisions: s.decisions.filter((x) => x.source === "meeting" && x.source_ref === m.id),
      can_edit: seriesRuns(p, series),
      preread: {
        state: m.preread_state,
        agent: agent
          ? { membership_id: agent.membership_id, name: agent.name, live: agent.live }
          : null,
        requested_at: m.preread_requested_at,
        note: m.preread_note,
        memory: null,
      },
      channel: channel ? { ...channel, topic: topicName(p.company, m) } : null,
      previous_meeting_id: at > 0 ? (list[at - 1]?.id ?? null) : null,
      next_meeting_id: at >= 0 ? (list[at + 1]?.id ?? null) : null,
    };
  }

  /** The live item's timer stops: its running time is added to its elapsed seconds. */
  function stopTimer(s: Store, m: Meeting) {
    if (!m.current_item_id || !m.item_started_at) return;
    const item = s.items.find((i) => i.id === m.current_item_id);
    if (item) {
      item.elapsed_s += Math.max(
        0,
        Math.round((d.now().getTime() - Date.parse(m.item_started_at)) / 1000),
      );
      item.updated_at = stamp();
    }
  }

  /** The meeting and its series, when the caller may see it (and run it, for `write`). */
  function meetingFor(
    p: Principal | null,
    id: string | undefined,
    write: boolean,
  ): { s: Store; m: Meeting; series: Series; p: Principal } | Response {
    if (!p) return notFound();
    const s = storeOf(p);
    const m = s.meetings.find((x) => x.id === id);
    const series = m ? s.series.find((x) => x.id === m.series_id) : undefined;
    if (!m || !series) return notFound();
    const held = heldBy(p);
    if (!seriesSees(p, series, held)) return notFound();
    if (write && !seriesRuns(p, series, held)) return forbidden();
    return { s, m, series, p };
  }
  function issueFor(
    p: Principal | null,
    id: string | undefined,
  ): { s: Store; i: Issue; p: Principal; held: Set<string> } | Response {
    if (!p) return notFound();
    const s = storeOf(p);
    const i = s.issues.find((x) => x.id === id);
    const held = heldBy(p);
    if (!i || !issueSees(p, i, held)) return notFound();
    return { s, i, p, held };
  }

  /** Puts `issue` at `position` in its column; both columns are renumbered. */
  function placeIssue(s: Store, issue: Issue, from: string | null, position: number) {
    const column = (status: string) =>
      s.issues
        .filter(
          (x) =>
            x.id !== issue.id &&
            x.status === status &&
            x.team_id === issue.team_id &&
            !x.archived_at,
        )
        .sort((a, b) => a.position - b.position);
    const target = column(issue.status);
    target.splice(Math.min(position, target.length), 0, issue);
    target.forEach((x, n) => {
      x.position = n;
    });
    if (from && from !== issue.status)
      column(from).forEach((x, n) => {
        x.position = n;
      });
  }

  function resolveIssue(
    p: Principal,
    s: Store,
    i: Issue,
    v: {
      kind: "task" | "decision" | "none";
      tasks?: { title: string; assignee_seat_id?: string | null; due_at?: string | null }[];
      decision?: { title?: string; decision: string; why?: string | null };
      note?: string | null;
    },
    meeting: { m: Meeting; series: Series } | null,
  ): void {
    const refs: Issue["resolution_refs"] = [];
    if (v.kind === "task")
      for (const t of v.tasks ?? [])
        refs.push({
          kind: "task",
          id: makeTodo(
            p,
            s,
            {
              title: t.title.trim(),
              assignee_seat_id: t.assignee_seat_id ?? null,
              due_at: t.due_at ?? null,
            },
            { kind: "issue", ref: i.id, series: meeting?.series ?? null, team_id: i.team_id },
          ),
        });
    if (v.kind === "decision" && v.decision) {
      const row = newDecision(
        p,
        s,
        {
          title: v.decision.title?.trim() || i.title,
          decision: v.decision.decision.trim(),
          why: v.decision.why ?? null,
          source: "issue",
          ref: i.id,
        },
        p,
      );
      refs.push({ kind: "decision", id: row.id });
    }
    const from = i.status;
    const at = stamp();
    Object.assign(i, {
      status: "resolved",
      resolution_kind: v.kind,
      resolution_refs: refs,
      resolution_note: v.note ?? null,
      resolved_at: at,
      updated_at: at,
    } satisfies Partial<Issue>);
    placeIssue(s, i, from, 0);
  }

  // Handlers --------------------------------------------------------------------------------

  const str = (v: unknown) => (typeof v === "string" ? v : undefined);
  const title = (v: unknown, path = "title") => {
    const t = typeof v === "string" ? v.trim() : "";
    return t && t.length <= 500 ? t : validation(`${path} must be 1–500 characters`);
  };
  const priorityOf = (v: unknown) =>
    v === undefined
      ? 2
      : Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 3
        ? (v as number)
        : null;
  const seatIn = (c: FakeCompany, v: unknown) =>
    v === null || v === undefined ? true : typeof v === "string" && !!seatOf(c, v);

  d.share?.({
    escalated: (p) =>
      storeOf(p)
        .issues.filter(
          (i) => i.escalated_to === "portfolio" && i.archived_at === null && openIssue(i),
        )
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
        .map((i) => issueView(p, i)),
    seedEscalated: (p, v) => {
      const s = storeOf(p);
      if (s.issues.some((i) => i.title === v.title)) return;
      const at = new Date(d.now().getTime() - v.daysAgo * DAY).toISOString();
      newIssue(
        p,
        s,
        {
          title: v.title,
          body: v.body,
          priority: v.priority,
          status: v.status,
          escalated_to: "portfolio",
          created_at: at,
          updated_at: at,
        },
        null,
      );
    },
  });

  return {
    "issues.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const held = heldBy(p);
      const team = url.searchParams.get("team_id");
      const closed = url.searchParams.get("include_closed") === "true";
      const archived = url.searchParams.get("archived") === "true";
      if (team && team !== "company" && !UUID.test(team)) return validation("team_id");
      const since = d.now().getTime() - 30 * DAY;
      const rank = (st: string) => {
        const k = ["identify", "discuss", "solve", "resolved", "dropped"].indexOf(st);
        return k === -1 ? 9 : k;
      };
      const rows = s.issues
        .filter((i) => issueSees(p, i, held))
        .filter((i) =>
          !team
            ? true
            : team === "company"
              ? i.team_id === null || i.escalated_to === "company"
              : i.team_id === team,
        )
        .filter((i) =>
          archived
            ? i.archived_at !== null
            : i.archived_at === null &&
              (openIssue(i) ||
                (closed &&
                  (i.status === "resolved" || i.status === "dropped") &&
                  Date.parse(i.resolved_at ?? i.updated_at) >= since)),
        )
        .sort(
          (a, b) =>
            rank(a.status) - rank(b.status) ||
            a.position - b.position ||
            a.priority - b.priority ||
            a.created_at.localeCompare(b.created_at),
        );
      const teams = d.teamsOf(p.company);
      const shown = teams.filter(
        (t) => p.manager || teamViewer(p, t, held) || rows.some((i) => i.team_id === t.id),
      );
      return json({
        data: rows.map((i) => issueView(p, i, held)),
        teams: shown.map((t) => ({ id: t.id, name: t.name, can_raise: canRaise(p, t.id, held) })),
        can_raise_company: canRaise(p, null, held),
      });
    },
    "issues.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const b = (body ?? {}) as Record<string, unknown>;
      const t = title(b.title);
      if (t instanceof Response) return t;
      const teamId = (b.team_id as string | null | undefined) ?? null;
      if (teamId !== null && !teamOf(p.company, teamId)) return validation("team_id: no such team");
      if (!seatIn(p.company, b.owner_seat_id)) return validation("owner_seat_id: no such seat");
      const priority = priorityOf(b.priority);
      if (priority === null) return validation("priority must be 1–3");
      const kinds = ["manual", "message", "task", "metric", "goal", "meeting"];
      if (b.source_kind !== undefined && !kinds.includes(String(b.source_kind)))
        return validation("source_kind");
      if (!canRaise(p, teamId)) return forbidden();
      const row = newIssue(
        p,
        s,
        {
          title: t,
          team_id: teamId,
          body: (b.body as string | null | undefined) ?? null,
          owner_seat_id: (b.owner_seat_id as string | null | undefined) ?? null,
          priority,
          source_kind: (b.source_kind as Issue["source_kind"] | undefined) ?? "manual",
          source_id: (b.source_id as string | null | undefined) ?? null,
          source_label: (b.source_label as string | null | undefined) ?? null,
        },
        p,
      );
      placeIssue(s, row, null, 0);
      return json(issueView(p, row), 201);
    },
    "issues.update": ({ principal, params, body }) => {
      const r = issueFor(principal, params.id);
      if (r instanceof Response) return r;
      const { s, i, p, held } = r;
      if (!issueEdits(p, i, held)) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.title !== undefined) {
        const t = title(b.title);
        if (t instanceof Response) return t;
        i.title = t;
      }
      if (b.body !== undefined) i.body = (b.body as string | null) ?? null;
      if (b.owner_seat_id !== undefined) {
        if (!seatIn(p.company, b.owner_seat_id)) return validation("owner_seat_id: no such seat");
        i.owner_seat_id = (b.owner_seat_id as string | null) ?? null;
      }
      if (b.priority !== undefined) {
        const pr = priorityOf(b.priority);
        if (pr === null) return validation("priority must be 1–3");
        i.priority = pr;
      }
      if (b.team_id !== undefined) {
        const teamId = (b.team_id as string | null) ?? null;
        if (teamId !== null && !teamOf(p.company, teamId))
          return validation("team_id: no such team");
        i.team_id = teamId;
      }
      const position =
        b.position === undefined
          ? undefined
          : Number.isInteger(b.position) && (b.position as number) >= 0
            ? (b.position as number)
            : null;
      if (position === null) return validation("position must be 0 or more");
      if (b.status !== undefined) {
        const st = String(b.status);
        if (![...COLUMNS, "dropped"].includes(st)) return validation("status");
        const from = i.status;
        if (st === "dropped") {
          i.status = "dropped";
          i.resolved_at = i.resolved_at ?? stamp();
        } else {
          if (from === "resolved" || from === "dropped") i.resolved_at = null;
          i.status = st as Issue["status"];
        }
        placeIssue(s, i, from, position ?? 0);
      } else if (position !== undefined) placeIssue(s, i, null, position);
      if (b.archived !== undefined) i.archived_at = b.archived ? (i.archived_at ?? stamp()) : null;
      i.updated_at = stamp();
      return json(issueView(p, i, held));
    },
    "issues.delete": ({ principal, params }) => {
      const r = issueFor(principal, params.id);
      if (r instanceof Response) return r;
      const { s, i, p, held } = r;
      if (!issueEdits(p, i, held)) return forbidden();
      s.issues = s.issues.filter((x) => x.id !== i.id);
      return noContent();
    },
    "issues.escalate": ({ principal, params, body }) => {
      const r = issueFor(principal, params.id);
      if (r instanceof Response) return r;
      const { i, p, held } = r;
      if (!issueEscalates(p, i, held)) return forbidden();
      const to = str((body as { to?: unknown } | undefined)?.to);
      if (to !== "company" && to !== "team" && to !== "portfolio")
        return validation("to: company, team or portfolio");
      if (to === "team" && i.team_id === null)
        return validation("to: the issue has no team to hand back to");
      // FOUNDER-OS: a company-level issue goes up to the portfolio (a manager or the head seat's
      // holder: `issueEscalates`); one at the portfolio is handed back with `to: company`.
      if (to === "portfolio" && i.team_id !== null && i.escalated_to === "team")
        return validation("to: escalate the issue to the company first");
      if (i.escalated_to !== to) {
        i.escalated_to = to;
        i.updated_at = stamp();
      }
      return json(issueView(p, i, held));
    },
    "issues.resolve": ({ principal, params, body }) => {
      const r = issueFor(principal, params.id);
      if (r instanceof Response) return r;
      const { s, i, p, held } = r;
      if (!issueEdits(p, i, held)) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      const kind = str(b.kind);
      if (kind !== "task" && kind !== "decision" && kind !== "none")
        return validation("kind: task, decision or none");
      if (i.status === "resolved" || i.status === "dropped")
        return conflict("the issue is already closed");
      const tasks = b.tasks as
        | { title?: unknown; assignee_seat_id?: unknown; due_at?: unknown }[]
        | undefined;
      if (kind === "task") {
        if (!Array.isArray(tasks) || tasks.length < 1 || tasks.length > 10)
          return validation("tasks: 1–10 to-dos");
        for (const [n, t] of tasks.entries()) {
          const tt = title(t.title, `tasks[${n}].title`);
          if (tt instanceof Response) return tt;
          if (!seatIn(p.company, t.assignee_seat_id))
            return validation(`tasks[${n}].assignee_seat_id: no such seat`);
        }
      }
      const decision = b.decision as
        | { title?: string; decision?: unknown; why?: string | null }
        | undefined;
      if (
        kind === "decision" &&
        !(typeof decision?.decision === "string" && decision.decision.trim())
      )
        return validation("decision.decision is required");
      resolveIssue(
        p,
        s,
        i,
        {
          kind,
          ...(tasks ? { tasks: tasks as { title: string }[] } : {}),
          ...(decision ? { decision: decision as { decision: string } } : {}),
          note: (b.note as string | null | undefined) ?? null,
        },
        null,
      );
      return json(issueView(p, i, held));
    },

    "meetingSeries.list": ({ principal: p }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const held = heldBy(p);
      return json({
        data: s.series
          .filter((x) => seriesSees(p, x, held))
          .sort(
            (a, b) =>
              Number(!!a.archived_at) - Number(!!b.archived_at) || a.name.localeCompare(b.name),
          )
          .map((x) => seriesView(p, s, x, held)),
        teams: d
          .teamsOf(p.company)
          .filter((t) => p.manager || teamViewer(p, t, held))
          .map((t) => ({
            id: t.id,
            name: t.name,
            can_create: p.manager || leadHolder(p, t, held),
          })),
      });
    },
    "meetingSeries.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const b = (body ?? {}) as Record<string, unknown>;
      const name = typeof b.name === "string" ? b.name.trim() : "";
      if (!name || name.length > 200) return validation("name must be 1–200 characters");
      const schedule = str(b.schedule) ?? "";
      if (!ruleOf(schedule)) return validation("schedule: not an RRULE the product supports");
      const teamId = (b.team_id as string | null | undefined) ?? null;
      const team = teamOf(p.company, teamId);
      if (teamId !== null && !team) return validation("team_id: no such team");
      const attendees = (b.attendees as string[] | undefined) ?? [];
      if (!Array.isArray(attendees) || attendees.some((a) => !seatOf(p.company, a)))
        return validation("attendees: seats of this company");
      if (!seatIn(p.company, b.facilitator_seat_id)) return validation("facilitator_seat_id");
      const held = heldBy(p);
      if (!(p.manager || (team && leadHolder(p, team, held)))) return forbidden();
      const series = newSeries(
        p,
        s,
        {
          name,
          schedule,
          team_id: teamId,
          kind: (b.kind as Series["kind"] | undefined) ?? "weekly",
          duration_minutes: (b.duration_minutes as number | undefined) ?? 60,
          attendees: [...new Set(attendees)],
          facilitator_seat_id: (b.facilitator_seat_id as string | null | undefined) ?? null,
          agenda_config: (b.agenda_config as Series["agenda_config"] | undefined) ?? {},
          channel_id:
            (b.channel_id as string | null | undefined) ??
            d.chat()?.teamChannel(p.company.id)?.id ??
            null,
          board_id: (b.board_id as string | null | undefined) ?? null,
          preread_by_membership_id:
            (b.preread_by_membership_id as string | null | undefined) ?? null,
        },
        p,
      );
      const first =
        str(b.first_meeting_at) ??
        nextOccurrence(p.company, series, d.now()) ??
        new Date(d.now().getTime() + DAY).toISOString();
      newMeeting(p, s, series, new Date(first).toISOString(), p);
      return json(seriesView(p, s, series, held), 201);
    },
    "meetingSeries.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const series = s.series.find((x) => x.id === params.id);
      const held = heldBy(p);
      if (!series || !seriesSees(p, series, held)) return notFound();
      if (!seriesRuns(p, series, held)) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.name !== undefined) {
        const name = typeof b.name === "string" ? b.name.trim() : "";
        if (!name) return validation("name must not be empty");
        series.name = name;
      }
      if (b.schedule !== undefined) {
        if (!ruleOf(String(b.schedule))) return validation("schedule: not a supported RRULE");
        series.schedule = String(b.schedule);
      }
      if (b.attendees !== undefined) {
        const a = b.attendees as string[];
        if (!Array.isArray(a) || a.some((x) => !seatOf(p.company, x)))
          return validation("attendees");
        series.attendees = [...new Set(a)];
      }
      if (b.facilitator_seat_id !== undefined) {
        if (!seatIn(p.company, b.facilitator_seat_id)) return validation("facilitator_seat_id");
        series.facilitator_seat_id = (b.facilitator_seat_id as string | null) ?? null;
      }
      for (const k of [
        "duration_minutes",
        "agenda_config",
        "channel_id",
        "board_id",
        "preread_by_membership_id",
      ] as const)
        if (b[k] !== undefined) Object.assign(series, { [k]: b[k] });
      if (b.archived !== undefined)
        series.archived_at = b.archived ? (series.archived_at ?? stamp()) : null;
      series.updated_at = stamp();
      return json(seriesView(p, s, series, held));
    },
    "meetings.list": ({ principal: p, params }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const series = s.series.find((x) => x.id === params.id);
      if (!series || !seriesSees(p, series)) return notFound();
      return json({ data: [...meetingsOf(s, series)].reverse().map((m) => summary(s, m)) });
    },
    "meetings.create": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const series = s.series.find((x) => x.id === params.id);
      const held = heldBy(p);
      if (!series || !seriesSees(p, series, held)) return notFound();
      if (!seriesRuns(p, series, held)) return forbidden();
      const at = str((body as { scheduled_at?: unknown } | undefined)?.scheduled_at);
      if (at !== undefined && Number.isNaN(Date.parse(at))) return validation("scheduled_at");
      const last = meetingsOf(s, series).at(-1);
      const when =
        at ??
        nextOccurrence(
          p.company,
          series,
          new Date(Math.max(d.now().getTime(), last ? Date.parse(last.scheduled_at) : 0)),
        );
      if (!when) return validation("the schedule has no next occurrence");
      const m = newMeeting(p, s, series, new Date(when).toISOString(), p);
      return json(detail(p, s, m), 201);
    },
    "meetings.get": ({ principal, params }) => {
      const r = meetingFor(principal, params.id, false);
      if (r instanceof Response) return r;
      return json(detail(r.p, r.s, r.m));
    },
    "meetings.update": ({ principal, params, body }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, p } = r;
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.notes !== undefined) {
        if (typeof b.notes !== "string" || b.notes.length > 20_000) return validation("notes");
        m.notes = b.notes;
      }
      if (b.preread_note !== undefined) {
        m.preread_note = (b.preread_note as string | null) ?? null;
        if (m.preread_note?.trim()) m.preread_state = "ready";
      }
      if (b.rating !== undefined) {
        const rating = b.rating as number;
        if (!Number.isInteger(rating) || rating < 1 || rating > 10)
          return validation("rating: 1–10");
        m.rating = rating;
      }
      if (b.current_item_id !== undefined) {
        const next = (b.current_item_id as string | null) ?? null;
        if (next && !s.items.some((i) => i.id === next && i.meeting_id === m.id))
          return validation("current_item_id: not an item of this meeting");
        if (m.state !== "live") return validation("current_item_id: the meeting is not live");
        stopTimer(s, m);
        m.current_item_id = next;
        m.item_started_at = next ? stamp() : null;
      }
      m.updated_at = stamp();
      return json(detail(p, s, m));
    },
    "meetings.buildAgenda": ({ principal, params }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, p } = r;
      if (m.state === "ended" || m.state === "cancelled")
        return conflict("the meeting is over: its agenda is kept as it was");
      buildAgenda(p, s, m);
      return json(detail(p, s, m));
    },
    "meetings.addItem": ({ principal, params, body }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, p } = r;
      const b = (body ?? {}) as Record<string, unknown>;
      const t = title(b.title);
      if (t instanceof Response) return t;
      const section = (str(b.section) ?? "custom") as Section;
      if (section !== "custom" && section !== "headlines") return validation("section");
      if (m.state === "ended" || m.state === "cancelled") return conflict("the meeting is over");
      const box = b.time_box_s === undefined ? 300 : Number(b.time_box_s);
      if (!Number.isInteger(box) || box < 0 || box > 36_000) return validation("time_box_s");
      const pos = Math.max(
        -1,
        ...itemsOf(s, m)
          .filter((i) => i.section === section)
          .map((i) => i.position),
      );
      newItem(p, s, m, { section, title: t, time_box_s: box, position: pos + 0.5 }, p);
      renumber(s, m);
      return json(detail(p, s, m), 201);
    },
    "meetings.updateItem": ({ principal, params, body }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, p } = r;
      const item = s.items.find((i) => i.id === params.item_id && i.meeting_id === m.id);
      if (!item) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.title !== undefined) {
        const t = title(b.title);
        if (t instanceof Response) return t;
        item.title = t;
      }
      if (b.time_box_s !== undefined) {
        const box = Number(b.time_box_s);
        if (!Number.isInteger(box) || box < 0 || box > 36_000) return validation("time_box_s");
        item.time_box_s = box;
      }
      if (b.elapsed_s !== undefined) {
        const e = Number(b.elapsed_s);
        if (!Number.isInteger(e) || e < 0) return validation("elapsed_s");
        item.elapsed_s = e;
      }
      if (b.done !== undefined) item.done_at = b.done ? (item.done_at ?? stamp()) : null;
      if (b.position !== undefined) {
        const to = Number(b.position);
        if (!Number.isInteger(to) || to < 0) return validation("position");
        // The position is the line's place within its section.
        const section = itemsOf(s, m).filter((i) => i.section === item.section && i.id !== item.id);
        section.splice(Math.min(to, section.length), 0, item);
        const start = Math.min(...section.map((i) => i.position));
        section.forEach((i, n) => {
          i.position = start + n;
        });
        renumber(s, m);
      }
      item.updated_at = stamp();
      return json(detail(p, s, m));
    },
    "meetings.removeItem": ({ principal, params }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m } = r;
      const item = s.items.find((i) => i.id === params.item_id && i.meeting_id === m.id);
      if (!item) return notFound();
      if (item.outcome_kind !== "none") return conflict("the line has an outcome");
      if (m.current_item_id === item.id) {
        stopTimer(s, m);
        m.current_item_id = null;
        m.item_started_at = null;
      }
      s.items = s.items.filter((i) => i.id !== item.id);
      renumber(s, m);
      return noContent();
    },
    "meetings.start": ({ principal, params }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, p } = r;
      if (m.state !== "scheduled") return conflict(`the meeting is ${m.state}`);
      if (!m.agenda_built_at) buildAgenda(p, s, m);
      const at = stamp();
      m.state = "live";
      m.started_at = at;
      m.current_item_id = itemsOf(s, m)[0]?.id ?? null;
      m.item_started_at = m.current_item_id ? at : null;
      m.updated_at = at;
      return json(detail(p, s, m));
    },
    // ACTIONS: a meeting not started yet is cancelled (kept); a live series schedules its next.
    "meetings.cancel": ({ principal, params }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, series, p } = r;
      if (m.state !== "scheduled") return conflict(`the meeting is ${m.state}`);
      m.state = "cancelled";
      m.updated_at = stamp();
      const later = meetingsOf(s, series).some(
        (x) => x.state !== "cancelled" && x.scheduled_at > m.scheduled_at,
      );
      if (!series.archived_at && !later) {
        const next = nextOccurrence(
          p.company,
          series,
          new Date(Math.max(d.now().getTime(), Date.parse(m.scheduled_at))),
        );
        if (next) newMeeting(p, s, series, next, p);
      }
      return json(detail(p, s, m));
    },
    "meetings.end": ({ principal, params, body }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, series, p } = r;
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.rating !== undefined) {
        const rating = b.rating as number;
        if (!Number.isInteger(rating) || rating < 1 || rating > 10)
          return validation("rating: 1–10");
      }
      if (m.state !== "live") return conflict(`the meeting is ${m.state}`);
      stopTimer(s, m);
      const at = stamp();
      Object.assign(m, {
        state: "ended",
        ended_at: at,
        current_item_id: null,
        item_started_at: null,
        updated_at: at,
        ...(typeof b.notes === "string" ? { notes: b.notes } : {}),
        ...(b.rating !== undefined ? { rating: b.rating as number } : {}),
      } satisfies Partial<Meeting>);
      const channelId = series.channel_id ?? d.chat()?.teamChannel(p.company.id)?.id ?? null;
      if (channelId) {
        const posted = d
          .chat()
          ?.postSummary(
            p.company.id,
            channelId,
            topicName(p.company, m),
            summaryOf(p, s, series, m),
          );
        m.summary_message_id = posted?.message_id ?? null;
      }
      if (!meetingsOf(s, series).some((x) => x.scheduled_at > m.scheduled_at)) {
        const next = nextOccurrence(
          p.company,
          series,
          new Date(Math.max(d.now().getTime(), Date.parse(m.scheduled_at))),
        );
        if (next) newMeeting(p, s, series, next, p);
      }
      return json(detail(p, s, m));
    },
    "meetings.recordOutcome": ({ principal, params, body }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, series, p } = r;
      const b = (body ?? {}) as Record<string, unknown>;
      const itemId = (b.item_id as string | null | undefined) ?? null;
      if (itemId && !s.items.some((i) => i.id === itemId && i.meeting_id === m.id))
        return validation("item_id: not an item of this meeting");
      if (m.state === "cancelled") return conflict("the meeting was cancelled");
      const kind = str(b.kind);
      if (kind === "task") {
        const t = title(b.title);
        if (t instanceof Response) return t;
        if (!seatIn(p.company, b.assignee_seat_id)) return validation("assignee_seat_id");
        const id = makeTodo(
          p,
          s,
          {
            title: t,
            assignee_seat_id: (b.assignee_seat_id as string | null | undefined) ?? null,
            due_at: (b.due_at as string | null | undefined) ?? null,
          },
          { kind: "meeting", ref: m.id, series, team_id: series.team_id },
        );
        newOutcome(p, s, m, { kind: "task", ref_id: id, title: t, item_id: itemId }, p);
      } else if (kind === "decision") {
        const text = typeof b.decision === "string" ? b.decision.trim() : "";
        if (!text) return validation("decision is required");
        const item = itemId ? s.items.find((i) => i.id === itemId) : undefined;
        const row = newDecision(
          p,
          s,
          {
            title: (typeof b.title === "string" && b.title.trim()) || item?.title || series.name,
            decision: text,
            why: (b.why as string | null | undefined) ?? null,
            source: "meeting",
            ref: m.id,
          },
          p,
        );
        newOutcome(p, s, m, { kind: "decision", ref_id: row.id, title: text, item_id: itemId }, p);
      } else if (kind === "issue_resolved") {
        const issue = s.issues.find((i) => i.id === b.issue_id);
        if (!issue || !issueSees(p, issue)) return validation("issue_id: no such issue");
        if (issue.status === "resolved" || issue.status === "dropped")
          return conflict("the issue is already closed");
        resolveIssue(
          p,
          s,
          issue,
          { kind: "none", note: (b.note as string | null | undefined) ?? null },
          {
            m,
            series,
          },
        );
        newOutcome(
          p,
          s,
          m,
          { kind: "issue_resolved", ref_id: issue.id, title: issue.title, item_id: itemId },
          p,
        );
      } else if (kind === "issue_raised") {
        const t = title(b.title);
        if (t instanceof Response) return t;
        if (!seatIn(p.company, b.owner_seat_id)) return validation("owner_seat_id");
        const pr = priorityOf(b.priority);
        if (pr === null) return validation("priority must be 1–3");
        const issue = newIssue(
          p,
          s,
          {
            title: t,
            team_id: series.team_id,
            owner_seat_id: (b.owner_seat_id as string | null | undefined) ?? null,
            priority: pr,
            source_kind: "meeting",
            source_id: m.id,
            source_label: `${series.name} · ${localDate(m.scheduled_at, zoneOf(p.company))}`,
          },
          p,
        );
        placeIssue(s, issue, null, 0);
        newOutcome(
          p,
          s,
          m,
          { kind: "issue_raised", ref_id: issue.id, title: t, item_id: itemId },
          p,
        );
      } else return validation("kind: task, decision, issue_resolved or issue_raised");
      return json(detail(p, s, m), 201);
    },
    "meetings.requestPreread": ({ principal, params }) => {
      const r = meetingFor(principal, params.id, true);
      if (r instanceof Response) return r;
      const { s, m, series, p } = r;
      if (m.state === "ended" || m.state === "cancelled") return conflict("the meeting is over");
      const agent = prereadAgent(p, series);
      const at = stamp();
      if (agent?.live) {
        m.preread_state = "requested";
        m.preread_wakeup_id = nextId(p.company, 9);
      } else m.preread_state = "pending";
      m.preread_requested_at = at;
      m.updated_at = at;
      return json(detail(p, s, m));
    },

    "vision.get": ({ principal: p }) => {
      if (!p) return notFound();
      return json(visionView(p, storeOf(p)));
    },
    "vision.update": ({ principal: p, body }) => {
      if (!p) return notFound();
      const s = storeOf(p);
      const b = (body ?? {}) as { sections?: Record<string, unknown>; version?: unknown };
      if (!b.sections || typeof b.sections !== "object") return validation("sections");
      for (const [k, v] of Object.entries(b.sections)) {
        if (!VISION.some((x) => x.key === k)) return validation(`sections.${k}: not a section`);
        if (v !== undefined && typeof v !== "string") return validation(`sections.${k}`);
      }
      if (!p.manager) return forbidden();
      if (b.version !== undefined && b.version !== s.vision.version)
        return conflict("the vision page was saved by someone else since you read it");
      for (const [k, v] of Object.entries(b.sections))
        if (typeof v === "string") s.vision.sections[k as VisionKey] = v;
      s.vision.page_id = s.vision.page_id ?? nextId(p.company, 8);
      s.vision.version += 1;
      s.vision.updated_at = stamp();
      return json(visionView(p, s));
    },
    // RECALL-KPIS-MIRROR: agree / object on a logged decision (memory is not reached here)
    "decisionStances.list": ({ principal: p, params }) => {
      if (!p) return notFound();
      const dec = storeOf(p).decisions.find((x) => x.id === params.id);
      return dec ? json(stancesView(p, dec)) : notFound();
    },
    "decisionStances.set": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const dec = storeOf(p).decisions.find((x) => x.id === params.id);
      if (!dec) return notFound();
      const b = (body ?? {}) as { stance?: unknown; note?: unknown };
      if (b.stance !== "ratify" && b.stance !== "dissent") return validation("stance");
      const note = typeof b.note === "string" && b.note.trim() ? b.note.trim() : null;
      if ((b.stance === "dissent") !== (note !== null)) return validation("note");
      const me = p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person");
      if (!me) return forbidden();
      const list = (stances.get(dec.id) ?? []).filter((x) => x.membership_id !== me.membership_id);
      list.push({
        id: nextId(p.company, 9),
        decision_id: dec.id,
        membership_id: me.membership_id,
        name: p.me.user.name,
        stance: b.stance,
        note,
        recall_state: "waiting",
        recall_detail: "Memory does not take ratify or dissent yet.",
        updated_at: stamp(),
      });
      stances.set(dec.id, list);
      return json(stancesView(p, dec));
    },
  };

  function stancesView(p: Principal, dec: DecisionRow): DecisionStances {
    const list = stances.get(dec.id) ?? [];
    const me = p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person");
    return {
      decision: dec,
      stances: list,
      mine: list.find((x) => x.membership_id === me?.membership_id) ?? null,
      can_stance: me !== undefined,
    };
  }

  function visionView(p: Principal, s: Store): VisionView {
    const now = d.now();
    const quarter = `${now.getUTCFullYear()}-Q${Math.floor(now.getUTCMonth() / 3) + 1}`;
    return {
      page_id: s.vision.page_id,
      version: s.vision.version,
      updated_at: s.vision.updated_at,
      sections: VISION.map((x) => ({ key: x.key, label: x.label, text: s.vision.sections[x.key] })),
      can_edit: p.manager,
      priorities: d
        .goalsOf(p.company)
        .filter(
          (g) =>
            g.kind === "priority" &&
            g.period_key === quarter &&
            (g.state === "open" || g.state === "achieved"),
        )
        .map((g) => ({
          id: g.id,
          title: g.title,
          track: g.track,
          seat_id: g.seat_id,
          state: g.state,
        })),
      quarter,
    };
  }
}
